import React, { useState, useEffect } from 'react';

const statuses = ['Active', 'Inactive'];

const UserFormModal = ({ show, onClose, onSave, user, availableRoles, availablePermissions = [], availableManagers = [], isSuperAdmin, loggedInUser }) => {
  const [form, setForm] = useState({
    name: '',
    username: '',
    password: '',
    role: availableRoles[0]?.roleName || '', // Extract roleName from role object
    status: statuses[0],
    email: '', // optional
    reportingTo: '', // Assign To (reporting manager)
    assignablePermissions: [], // sub-user assignable permissions
  });
  const [usernameError, setUsernameError] = useState('');

  // Find the selected role object from availableRoles
  // availableRoles is now an array of role objects with { roleName, userTypeConfig, ... }
  const selectedRoleObject = availableRoles.find(r =>
    r.roleName && form.role && r.roleName.toLowerCase().trim() === form.role.toLowerCase().trim()
  );

  // Extract userTypeConfig from the selected role
  const configSchema = selectedRoleObject?.userTypeConfig;

  useEffect(() => {
    // Filter out current user's role from available roles
    // availableRoles is now an array of role objects, so filter by roleName
    const filteredRoles = availableRoles.filter(roleObj =>
      roleObj.roleName && loggedInUser?.role &&
      roleObj.roleName.toLowerCase().trim() !== loggedInUser.role.toLowerCase().trim()
    );

    if (user) {
      // Extract sub-type/partner model from userConfig if available
      let userConfigValue = 'CPD'; // Default fallback

      // Find the role object for this user's role to get userTypeConfig
      const userRoleObject = availableRoles.find(r =>
        r.roleName && user.role && r.roleName.toLowerCase().trim() === user.role.toLowerCase().trim()
      );

      if (userRoleObject?.userTypeConfig?.hasSubType && userRoleObject.userTypeConfig.subTypeKey) {
        const key = userRoleObject.userTypeConfig.subTypeKey;
        if (user.userConfig && user.userConfig[key]) {
          userConfigValue = user.userConfig[key];
        } else if (user[key]) {
          // Fallback if stored flat
          userConfigValue = user[key];
        }
      } else {
        // If no config loaded yet, try known keys for backward compatibility
        userConfigValue = user.userConfig?.partnerModel || user.partnerModel || 'CPD';
      }

      setForm({
        assignablePermissions: user.assignablePermissions || [],
        reportingTo: user.reportingTo || '',
        ...user,
        password: '',
        partnerModel: userConfigValue
      });
    } else {
      // For new user: Super Admin can set reportingTo, others auto-set to self
      const defaultReportingTo = isSuperAdmin ? '' : (loggedInUser?.id || '');
      // Set default role to first available role's name
      const defaultRole = filteredRoles[0]?.roleName || '';
      setForm({
        name: '',
        username: '',
        password: '',
        role: defaultRole,
        status: statuses[0],
        email: '',
        reportingTo: defaultReportingTo,
        assignablePermissions: [],
        partnerModel: 'CPD' // Default
      });
    }
  }, [user, availableRoles, loggedInUser, isSuperAdmin]);

  const validateUsername = (username) => {
    const re = /^[a-zA-Z0-9_-]{3,30}$/;
    return re.test(username);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (name === 'username') {
      if (!validateUsername(value)) {
        setUsernameError('Username must be 3-30 characters and can only contain letters, numbers, underscores, and hyphens.');
      } else {
        setUsernameError('');
      }
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateUsername(form.username)) {
      setUsernameError('Username must be 3-30 characters and can only contain letters, numbers, underscores, and hyphens.');
      return;
    }
    const submitForm = { ...form };
    delete submitForm.permissions;
    if (!submitForm.password) {
      delete submitForm.password;
    }
    // If reportingTo is empty string, set to null for backend
    if (submitForm.reportingTo === '') {
      submitForm.reportingTo = null;
    }
    onSave(submitForm);
  };

  if (!show) return null;

  return (
    <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog" style={{ maxWidth: '700px' }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{user ? 'Edit User' : 'Create User'}</h5>
            <button type="button" className="close" onClick={onClose}>&times;</button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
              <div className="form-group">
                <label>Name</label>
                <input type="text" className="form-control" name="name" value={form.name} onChange={handleChange} required autoComplete="off" />
              </div>
              <div className="form-group">
                <label>Username</label>
                <input type="text" className="form-control" name="username" value={form.username} onChange={handleChange} required autoComplete="off" minLength={3} maxLength={30} pattern="[a-zA-Z0-9_-]+" />
                {usernameError && <small className="text-danger">{usernameError}</small>}
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" className="form-control" name="password" value={form.password} onChange={handleChange} required={!user} autoComplete="new-password" />
              </div>
              <div className="form-group">
                <label>Role *</label>
                {availableRoles.length > 0 ? (
                  <select className="form-control" name="role" value={form.role} onChange={handleChange} required>
                    {availableRoles
                      .filter(roleObj =>
                        // Filter out current user's role (compare by roleName)
                        roleObj.roleName && loggedInUser?.role &&
                        roleObj.roleName.toLowerCase().trim() !== loggedInUser.role.toLowerCase().trim()
                      )
                      .map(roleObj => <option key={roleObj.roleName} value={roleObj.roleName}>{roleObj.roleName}</option>)}
                  </select>
                ) : (
                  <>
                    <input
                      type="text"
                      className="form-control"
                      name="role"
                      value={form.role}
                      onChange={handleChange}
                      placeholder="Enter role name"
                      required
                    />
                    <small className="text-muted">
                      Roles could not be loaded. Please enter the role name manually.
                    </small>
                  </>
                )}
              </div>

              {/* Dynamic Configuration based on Role */}
              {configSchema && configSchema.hasSubType && (
                <div className="form-group" style={{ backgroundColor: '#f9f9f9', padding: '15px', border: '1px solid #eee', borderRadius: '4px' }}>
                  <label style={{ color: '#2c3e50' }}>{configSchema.subTypeLabel || 'Configuration'}</label>
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ fontWeight: 'normal', fontSize: '13px' }}>Select Type</label>
                    <select
                      className="form-control"
                      name="partnerModel" // Keeping consistent name for now, logic on save handles mapping
                      value={form.partnerModel || (configSchema.subTypeOptions?.[0]?.value) || ''}
                      onChange={handleChange}
                    >
                      {configSchema.subTypeOptions?.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Assign To dropdown - Only for Super Admin (both create and edit) */}
              {isSuperAdmin && (
                <div className="form-group">
                  <label>Assign To (Reporting Manager)</label>
                  <select
                    className="form-control"
                    name="reportingTo"
                    value={form.reportingTo || ''}
                    onChange={handleChange}
                  >
                    <option value="">No Manager (Top Level)</option>
                    {availableManagers
                      .filter(manager => {
                        // Exclude current user being edited (can't assign to self)
                        if (manager.id === user?.id) return false;
                        
                        // Exclude users with same role as selected role (same level users can't report to each other)
                        // Trim whitespace for comparison as role names might have trailing spaces
                        const selectedRole = (form.role || '').trim();
                        const managerRole = (manager.role || '').trim();
                        if (selectedRole && managerRole && selectedRole.toLowerCase() === managerRole.toLowerCase()) {
                          return false;
                        }
                        
                        return true;
                      })
                      .map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name} ({manager.role})
                        </option>
                      ))}
                  </select>
                  <small className="text-muted">
                    Select who this user will report to. Leave empty for top-level users. Users with same role cannot be assigned.
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>Status</label>
                <select className="form-control" name="status" value={form.status} onChange={handleChange}>
                  {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Email (optional)</label>
                <input type="email" className="form-control" name="email" value={form.email} onChange={handleChange} autoComplete="off" />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={!!usernameError}>{user ? 'Update' : 'Create'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal; 